/**
 * @fullvsl/dashboard/analytics-fixture — deterministic fixture analytics (FUL-17).
 *
 * The MVP directive: use fixture/sample data, do NOT block on backend aggregation
 * or external access. To make the dashboard reflect exactly what the backend API
 * would return, we generate a representative `AnalyticsEvent[]` here and derive
 * the overview + breakdown by calling the REAL backend aggregation functions
 * (`computeOverview` / `computeBreakdown`). Single source of truth → zero drift
 * between this UI and `GET /api/v1/vsls/:id/analytics`(+ `/breakdown`).
 *
 * The stream is seeded (mulberry32) so tests + screenshots are reproducible.
 */
import type {
  AnalyticsBreakdown,
  AnalyticsEvent,
  AnalyticsOverview,
} from "../../contracts/src/contracts";
import type { AnalyticsEventType } from "../../contracts/src/contracts";
import { computeBreakdown, computeOverview } from "../../backend/src/aggregation";

/** VSL the fixture analytics are scoped to. */
export const FIXTURE_VSL = {
  id: "vsl-q3-launch",
  name: "VSL de lançamento do produto",
  aspectRatio: "16:9" as const,
  sourceMode: "html5" as const,
  /** Video duration used to translate watched % into plausible clientTs. */
  durationSec: 612,
};

/** Seeded PRNG (mulberry32) — deterministic across runs/Node versions. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type Device = "desktop" | "mobile" | "tablet";
type Source =
  | "facebook_ads"
  | "google_ads"
  | "organic_search"
  | "email_broadcast"
  | "youtube_referral";
type Aspect = "16:9" | "9:16" | "1:1" | "4:5" | "3:4";

/** Weighted pick helper. */
function weighted<T>(rng: () => number, table: [T, number][]): T {
  const total = table.reduce((s, [, w]) => s + w, 0);
  let r = rng() * total;
  for (const [v, w] of table) {
    r -= w;
    if (r <= 0) return v;
  }
  return table[table.length - 1][0];
}

const DEVICE_TABLE: [Device, number][] = [
  ["mobile", 0.62],
  ["desktop", 0.31],
  ["tablet", 0.07],
];
const SOURCE_TABLE: [Source, number][] = [
  ["facebook_ads", 0.34],
  ["google_ads", 0.21],
  ["organic_search", 0.18],
  ["email_broadcast", 0.17],
  ["youtube_referral", 0.1],
];
const ASPECT_TABLE: [Aspect, number][] = [
  ["16:9", 0.71],
  ["9:16", 0.16],
  ["1:1", 0.07],
  ["4:5", 0.04],
  ["3:4", 0.02],
];

let eventCounter = 0;
function uuid(rng: () => number): string {
  // Deterministic-ish UUID v4 shape (good enough for fixture ids; not for crypto).
  const hex = (_n: number) =>
    Math.floor(rng() * 0xffffffff).toString(16).padStart(8, "0");
  eventCounter += 1;
  return `${hex(1)}-${hex(2).slice(0, 4)}-4${hex(3).slice(0, 3)}-8${hex(
    4,
  ).slice(0, 3)}-${hex(5)}${eventCounter.toString(16).slice(-4).padStart(4, "0")}`;
}

function iso(base: number, offsetMs: number): string {
  return new Date(base + offsetMs).toISOString();
}

function pushEvent(
  out: AnalyticsEvent[],
  rng: () => number,
  args: {
    vslId: string;
    visitorId: string;
    sessionId: string;
    baseTs: number;
    type: AnalyticsEventType;
    atMs: number;
    value?: number;
    meta?: Record<string, unknown>;
  },
): void {
  out.push({
    eventId: uuid(rng),
    vslId: args.vslId,
    sessionId: args.sessionId,
    visitorId: args.visitorId,
    type: args.type,
    clientTs: iso(args.baseTs, args.atMs),
    ...(args.value != null ? { value: args.value } : {}),
    ...(args.meta && Object.keys(args.meta).length
      ? { meta: args.meta }
      : {}),
  });
}

export interface FixtureAnalytics {
  vslId: string;
  events: AnalyticsEvent[];
  overview: AnalyticsOverview;
  breakdown: AnalyticsBreakdown;
}

/**
 * Generate the fixture analytics dataset.
 *
 * @param sessions approximate number of visitor sessions (impressions).
 * @param seed     PRNG seed for reproducibility.
 */
export function generateFixtureAnalytics(
  sessions = 360,
  seed = 20260702,
): FixtureAnalytics {
  const rng = mulberry32(seed);
  const events: AnalyticsEvent[] = [];
  const vslId = FIXTURE_VSL.id;
  // Spread impressions across a 14-day window ending "now".
  const now = Date.UTC(2026, 6, 2, 14, 0, 0);
  const windowMs = 14 * 24 * 60 * 60 * 1000;

  let visitorN = 0;
  for (let s = 0; s < sessions; s++) {
    visitorN += 1;
    const visitorId = `v_${visitorN.toString(36)}`;
    const sessionId = `s_${visitorN.toString(36)}`;
    const device = weighted(rng, DEVICE_TABLE);
    const source = weighted(rng, SOURCE_TABLE);
    const aspect = weighted(rng, ASPECT_TABLE);
    const baseTs = now - Math.floor(rng() * windowMs);

    // Impression (first-seen device/source/aspect drive the breakdown).
    pushEvent(events, rng, {
      vslId,
      visitorId,
      sessionId,
      baseTs,
      type: "impression",
      atMs: 0,
      meta: { source, device, aspectRatio: aspect, sourceMode: FIXTURE_VSL.sourceMode },
    });

    // ~80% have smart autoplay configured → autoplay_impression tracked.
    if (rng() < 0.8) {
      pushEvent(events, rng, {
        vslId,
        visitorId,
        sessionId,
        baseTs,
        type: "autoplay_impression",
        atMs: 1200,
        meta: { source, device, sourceMode: FIXTURE_VSL.sourceMode },
      });
    }

    // Play rate ≈ 0.62 of impressions (the visitor clicks to play/unmute).
    if (rng() >= 0.62) continue;
    pushEvent(events, rng, {
      vslId,
      visitorId,
      sessionId,
      baseTs,
      type: "play",
      atMs: 2000,
      value: 0,
      meta: { positionSec: 0, pct: 0, source, device, sourceMode: FIXTURE_VSL.sourceMode },
    });

    // Per-viewer max watched %. Draw from a retention-like distribution:
    // steep early drop, long tail, ~15% true completers (maxPct = 100). This
    // shapes the retention curve AND guarantees `completed` events fire for the
    // top tier (VSL completion typically lands 12–20%).
    const u = rng();
    let maxPct: number;
    if (u < 0.16) maxPct = 2 + rng() * 8; // bounce early (2–10%)
    else if (u < 0.42) maxPct = 10 + rng() * 35; // 10–45%
    else if (u < 0.66) maxPct = 45 + rng() * 20; // 45–65%
    else if (u < 0.85) maxPct = 65 + rng() * 20; // 65–85%
    else maxPct = 100; // completers — reach the end of the video
    maxPct = Math.min(100, Math.round(maxPct));

    // Milestones reached (max-watched). 25/50/75/100.
    for (const m of [25, 50, 75, 100] as const) {
      if (maxPct >= m) {
        pushEvent(events, rng, {
          vslId,
          visitorId,
          sessionId,
          baseTs,
          type: "milestone",
          atMs: 2000 + (m / 100) * FIXTURE_VSL.durationSec * 1000,
          value: m,
          meta: {
            milestone: m,
            pct: m,
            positionSec: Math.round((m / 100) * FIXTURE_VSL.durationSec),
            source,
            device,
          },
        });
      }
    }
    if (maxPct >= 100) {
      pushEvent(events, rng, {
        vslId,
        visitorId,
        sessionId,
        baseTs,
        type: "completed",
        atMs: 2000 + FIXTURE_VSL.durationSec * 1000,
        meta: { pct: 100, source, device },
      });
    }

    // CTA reveals at the 50% watched threshold (matches the demo CTA config).
    if (maxPct >= 50) {
      pushEvent(events, rng, {
        vslId,
        visitorId,
        sessionId,
        baseTs,
        type: "cta_shown",
        atMs: 2000 + 0.5 * FIXTURE_VSL.durationSec * 1000,
        meta: {
          positionSec: Math.round(0.5 * FIXTURE_VSL.durationSec),
          pct: 50,
          revealMode: "percent",
          basedOn: "maxWatched",
          destinationType: "url",
        },
      });
      // Conversion proxy: ~34% of those shown click.
      if (rng() < 0.34) {
        pushEvent(events, rng, {
          vslId,
          visitorId,
          sessionId,
          baseTs,
          type: "cta_clicked",
          atMs: 2000 + (0.5 + rng() * 0.15) * FIXTURE_VSL.durationSec * 1000,
          meta: {
            positionSec: Math.round((0.5 + rng() * 0.15) * FIXTURE_VSL.durationSec),
            pct: Math.round(50 + rng() * 15),
            revealMode: "percent",
            basedOn: "maxWatched",
            destinationType: "url",
          },
        });
      }
    }

    // Occasional pause mid-watch.
    if (maxPct > 30 && rng() < 0.25) {
      pushEvent(events, rng, {
        vslId,
        visitorId,
        sessionId,
        baseTs,
        type: "pause",
        atMs: 2000 + rng() * (maxPct / 100) * FIXTURE_VSL.durationSec * 1000,
        meta: {
          positionSec: Math.round(rng() * (maxPct / 100) * FIXTURE_VSL.durationSec),
          pct: Math.round(rng() * maxPct),
          source,
          device,
        },
      });
    }

    // Returning visitors: ~22% get a resume prompt; ~70% of those accept.
    if (maxPct > 40 && maxPct < 100 && rng() < 0.22) {
      const returnBase = baseTs + 2 * 24 * 60 * 60 * 1000;
      pushEvent(events, rng, {
        vslId,
        visitorId,
        sessionId: `${sessionId}_r`,
        baseTs: returnBase,
        type: "resume_offered",
        atMs: 0,
        meta: { source, device, positionSec: Math.round((maxPct / 100) * FIXTURE_VSL.durationSec) },
      });
      if (rng() < 0.7) {
        pushEvent(events, rng, {
          vslId,
          visitorId,
          sessionId: `${sessionId}_r`,
          baseTs: returnBase,
          type: "resume_accepted",
          atMs: 1500,
          meta: { source, device },
        });
      }
    }
  }

  events.sort((a, b) => (a.clientTs < b.clientTs ? 1 : a.clientTs > b.clientTs ? -1 : 0));

  const overview = computeOverview(events, vslId);
  const breakdown = computeBreakdown(events, vslId);
  return { vslId, events, overview, breakdown };
}
